import { useState } from "react";
import { motion } from "framer-motion";
import { RECEIPTS, APP_URL } from "./data";
import { Reveal, SectionHead } from "./Primitives";

const QUERIES = [["KRAS", "G12D"], ["BRAF", "V600E"]];

const Receipt = ({ r, q, i }) => (
  <Reveal delay={i * 0.08} className="min-w-0">
    <motion.div
      key={q}
      initial={{ opacity: 0.4, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: i * 0.07 }}
      data-testid={`triage-receipt-${i}`}
      className="relative h-full rounded-lg border border-dashed border-[#c9d2cd] dark:border-[var(--line)] bg-[var(--surface)] p-6"
    >
      <div className="flex items-center justify-between font-mono text-xs text-[var(--muted)]">
        <span>RECEIPT {r.n}</span>
        <span className="text-[var(--teal)]">{q}</span>
      </div>
      <h3 className="font-display mt-6 text-2xl font-bold tracking-tight">{r.t}</h3>
      <ul className="mt-5 space-y-3">
        {r.rows.map((row) => (
          <li key={row} className="flex gap-3 text-sm text-[var(--muted)] leading-relaxed">
            <span className="mt-2 h-1 w-3 shrink-0 bg-[var(--teal)]" />{row}
          </li>
        ))}
      </ul>
      <div className="absolute -bottom-px left-6 right-6 h-px bg-[repeating-linear-gradient(90deg,var(--line)_0_6px,transparent_6px_12px)]" />
    </motion.div>
  </Reveal>
);

export const Triage = () => {
  const [q, setQ] = useState(0);
  const label = QUERIES[q].join(" ");
  return (
    <section id="triage" data-testid="triage-section" className="relative py-24 md:py-32 bg-[var(--surface-2)] overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 items-end">
          <SectionHead eyebrow="Triage · /predict" title={<>Type a substitution. <span className="text-[var(--amber)]">Get four receipts.</span></>} body="Triage measures every matching comparable deposition against the current reference — then shows pathogenicity, family context and provenance as separate layers." />
          <Reveal className="lg:justify-self-end w-full lg:w-auto">
            <div className="flex items-center gap-2 rounded-lg border border-[var(--line)] bg-[var(--surface)] p-1.5 w-full lg:w-auto">
              {QUERIES.map(([g, m], k) => (
                <button key={g} onClick={() => setQ(k)} data-testid={`triage-query-${g.toLowerCase()}`}
                  className={`relative flex-1 lg:flex-none rounded-md px-5 py-2.5 font-mono text-sm transition-colors ${q === k ? "text-[var(--on-teal)]" : "text-[var(--muted)] hover:text-[var(--teal)]"}`}>
                  {q === k && <motion.span layoutId="triage-pill" className="absolute inset-0 rounded-md bg-[var(--teal)]" />}
                  <span className="relative">{g} {m}</span>
                </button>
              ))}
            </div>
          </Reveal>
        </div>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {RECEIPTS.map((r, i) => <Receipt key={r.n} r={r} q={label} i={i} />)}
        </div>
        <Reveal className="mt-10 flex flex-col md:flex-row md:items-center justify-between gap-4 border-t border-[var(--line)] pt-6">
          <p className="text-sm text-[var(--muted)] max-w-2xl">The headline is structural-QC wording. Missing evidence produces an incomplete result — never invented evidence. Receipts above show what each layer contains; run it live for real values.</p>
          <a href={`${APP_URL}predict`} target="_blank" rel="noreferrer" data-testid="triage-run-live-link" className="shrink-0 rounded-md border border-[var(--teal)] px-5 py-2.5 text-sm font-semibold text-[var(--teal)] hover:bg-[var(--teal)] hover:text-[var(--on-teal)] transition-colors">Run {label} live →</a>
        </Reveal>
      </div>
    </section>
  );
};
